import { ref } from 'vue';
import type { Router, RouteLocationNormalizedLoaded } from 'vue-router';

// Shared arm/confirm state for an onBeforeRouteLeave guard that must pause an
// accidental exit (back arrow, browser/hardware back) behind a confirmation
// dialog, while letting navigations the client didn't choose (room closed,
// host ending the session) through unprompted.
//
// Usage inside a guard: call `skipNextConfirm()` right before any programmatic
// navigation that shouldn't be confirmed, and call `guardExit(to)` at each
// point the guard would otherwise act on the leave — it arms the dialog and
// returns false the first time, then true once skipped or confirmed.
export function useClassroomExitConfirm(router: Router) {
  const showExitConfirm = ref(false);
  let pendingTarget: RouteLocationNormalizedLoaded | null = null;
  let exitConfirmed = false;
  let skipOnce = false;

  const guardExit = (to: RouteLocationNormalizedLoaded): boolean => {
    if (skipOnce || exitConfirmed) {
      skipOnce = false;
      exitConfirmed = false;
      return true;
    }
    pendingTarget = to;
    showExitConfirm.value = true;
    return false;
  };

  const skipNextConfirm = () => {
    skipOnce = true;
  };

  const confirmExit = () => {
    showExitConfirm.value = false;
    exitConfirmed = true;
    const target = pendingTarget;
    pendingTarget = null;
    if (target) router.push(target.fullPath);
  };

  // For a `<keep-alive>`d view: nothing should carry an armed bypass into a
  // freshly (re)connected room.
  const reset = () => {
    exitConfirmed = false;
    skipOnce = false;
  };

  return { showExitConfirm, guardExit, skipNextConfirm, confirmExit, reset };
}
